import React, {forwardRef} from "react";
import {Autocomplete} from 'mui-rff';
import {Autocomplete as MuiAutocomplete, createFilterOptions, TextField} from "@mui/material";
import classnames from "classnames";

const filter = createFilterOptions();

const oldReason = (reason) => {
    switch (reason) {
        case 'selectOption':
            return 'select-option';
        case 'createOption':
            return 'create-option';
        case 'removeOption':
            return 'remove-option';
        case 'clear':
            return 'clear';
        default:
            return '';
    }
};

export const OutlinedAutoCompleteForm = ({
                                             options,
                                             placeholder = 'type...',
                                             label,
                                             ariaLabel = 'input',
                                             required = false,
                                             fullWidth = true,
                                             helperText,
                                             name,
                                             multiple,
                                             onClose,
                                             variant = 'outlined',
                                             disableCloseOnSelect,
                                             selectOnFocus,
                                             clearOnBlur,
                                             handleHomeEndKeys,
                                             freeSolo = true,
                                             loading,
                                             loadingText = 'loading...',
                                             filterOptions,
                                             addNewText = (input) => `Add ${input}`,
                                             onChange,
                                             renderOption,
                                             onKeywordsChange,
                                             inputRef,
                                             // keywords,
                                             textFieldProps,
                                             disabled = false,
                                             ...props
                                         }) => {

    const defaultFilterOptions = (options, params) => {
        const filtered = filter(options, params);
        if (freeSolo) {
            if (params.inputValue !== '') {
                filtered.push({
                    value: {title: params.inputValue, id: -1},
                    title: addNewText(params.inputValue),
                    id: -1,
                });
            }
        }
        return filtered;
    }

    const getOptionLabel = (option) => {
        if (typeof option === 'string') return option;
        // Add "xxx" option created dynamically
        if (option.id === -1) {
            return option.value.title;
        }
        // Regular option
        return option.title;
    }

    const defaultRenderOption = (props, option, state) => {
        const content = renderOption?.(props, option, state) ?? function () {
            if (!option) return '';
            if (typeof option === 'string') return (
                <p className={classnames('mx-2 p-2', props.className)}>{option}</p>
            );
            // Add "xxx" option created dynamically
            if (option.id === -1) return (
                <p className='mx-2 p-2'>
                    {option.title ?? option.value.title ?? option.value.label}
                </p>
            )
            // Regular option
            return <p className='mx-2 p-2'>
                {option.title ?? option.label}
            </p>
        }();
        return (
            <li {...props} key={props.id}>
                {content}
            </li>
        );
    }

    const defaultOnChange = (e, value, _reason) => {
        onChange?.(e, value, oldReason(_reason));
    }

    const defaultOnClose = (e, _reason) => {
        onClose?.(e, oldReason(_reason));
    }

    return (
        <Autocomplete
            disabled={disabled}
            onChange={defaultOnChange}
            onClose={defaultOnClose}
            loading={loading}
            loadingText={loadingText}
            placeholder={placeholder}
            aria-label={ariaLabel}
            aria-multiline={multiple}
            label={label}
            name={name}
            freeSolo={freeSolo}
            multiple={multiple}
            variant={variant}
            required={required}
            options={options}
            filterOptions={filterOptions ?? defaultFilterOptions}
            selectOnFocus={selectOnFocus}
            clearOnBlur={clearOnBlur}
            handleHomeEndKeys={handleHomeEndKeys}
            getOptionValue={(option) => option}
            getOptionLabel={getOptionLabel}
            fullWidth={fullWidth}
            disableCloseOnSelect={disableCloseOnSelect}
            textFieldProps={{
                ...textFieldProps,
                name: name,
                inputRef: inputRef,
                onChange: onKeywordsChange,
                variant: variant,
                placeholder: placeholder,
                'aria-label': ariaLabel,
                label: label,
            }}
            renderOption={defaultRenderOption}
            helperText={helperText}
            {...props}
        />
    );
}

export const OutlinedAutoComplete = forwardRef(({
                                                    focusOnRender = false,
                                                    options,
                                                    placeholder = 'type...',
                                                    label,
                                                    ariaLabel = 'input',
                                                    fullWidth = true,
                                                    name,
                                                    onSelect,
                                                    onRemove,
                                                    onOptionUnselected,
                                                    multiple,
                                                    variant = 'outlined',
                                                    disableCloseOnSelect,
                                                    selectOnFocus,
                                                    clearOnBlur,
                                                    handleHomeEndKeys,
                                                    freeSolo = true,
                                                    loading,
                                                    loadingText = 'loading...',
                                                    filterOptions,
                                                    addNewText = (input) => `Add ${input}`,
                                                    onChange,
                                                    renderOption,
                                                    inputValue,
                                                    value,
                                                    className,
                                                    getOptionSelected,
                                                    endAdornment,
                                                    defaultEndAdornmentProps,
                                                    includeDefaultEndAdornment = false,
                                                    inputRef,
                                                    error = false,
                                                    disabled = false,
                                                    onClose,
                                                    ...props
                                                }, ref) => {


    const defaultFilterOptions = (options, params) => {
        const filtered = filter(options, params);
        if (freeSolo) {
            if (params.inputValue !== '') {
                filtered.push({
                    value: {title: params.inputValue, id: -1},
                    title: addNewText(params.inputValue),
                    id: -1,
                });
            }
        }
        return filtered;
    }

    const getOptionLabel = (option) => {
        if (typeof option === 'string') return option;
        // Add "xxx" option created dynamically
        if (option.id === -1) return option.value.title;
        // Regular option
        return option.title;
    }

    const defaultRenderOption = (props, option, state) => {
        const content = renderOption?.(props, option, state) ?? function () {
            if (!option) return '';
            if (typeof option === 'string') return (
                <p className={classnames('mx-2 p-2', props.className)}>{option}</p>
            );
            // Add "xxx" option created dynamically
            if (option.id === -1) return (
                <p className='mx-2 p-2'>
                    {option.value.title ?? option.value.label}
                </p>
            )
            // Regular option
            return <p className='mx-2 p-2'>
                {option.title ?? option.label}
            </p>
        }();
        return (
            <li {...props} key={props.id}>
                {content}
            </li>
        );
    }

    const onSelectInner = (e, value, _reason) => {
        const reason = oldReason(_reason);
        e.stopPropagation();
        e.preventDefault();
        if (reason === 'select-option') {
            if (onSelect) onSelect(e, value);
        }
        if (reason === 'remove-option')
            if (onOptionUnselected) onOptionUnselected(e, value)
        if (reason === 'clear') {
            if (onRemove) onRemove()
        }
    }

    const defaultOnClose = (e, _reason) => {
        onClose?.(e, oldReason(_reason));
    }

    return (
        <MuiAutocomplete
            ref={ref}
            {...(getOptionSelected ? {isOptionEqualToValue: getOptionSelected} : {})}
            className={className}
            onClose={defaultOnClose}
            onChange={onSelectInner}
            loading={loading}
            loadingText={loadingText}
            placeholder={placeholder}
            aria-label={ariaLabel}
            aria-multiline={multiple}
            label={label}
            name={name}
            value={value}
            freeSolo={freeSolo}
            multiple={multiple}
            variant={variant}
            options={options}
            filterOptions={filterOptions ?? defaultFilterOptions}
            selectOnFocus={selectOnFocus}
            clearOnBlur={clearOnBlur}
            handleHomeEndKeys={handleHomeEndKeys}
            getOptionLabel={getOptionLabel}
            fullWidth={fullWidth}
            disabled={disabled}
            disableCloseOnSelect={disableCloseOnSelect}
            renderInput={({..._props}) => {
                return (
                    <TextField
                        {..._props}
                        inputRef={inputRef}
                        disabled={disabled}
                        onChange={onChange}
                        variant={variant}
                        placeholder={placeholder}
                        aria-label={ariaLabel}
                        label={label}
                        error={error}
                        InputProps={{
                            ..._props.InputProps,
                            ...(props.InputProps ?? {}),
                            endAdornment: (
                                <>
                                    {endAdornment}
                                    {
                                        includeDefaultEndAdornment &&
                                        React.cloneElement(_props.InputProps.endAdornment, defaultEndAdornmentProps)
                                    }
                                </>
                            ),
                        }}
                        value={inputValue}
                    />
                )
            }}
            renderOption={defaultRenderOption}
            {...props}
        />
    );
})
